import { Switch } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function Configuration({ data, onChangeField, onCompleted }) {
    const formSchema = z.object({
        assetsAttributes: z.array(z.object({
            create_work_orders: z.boolean(),
            create_markers: z.boolean(),
            _destroy: z.boolean().optional()
        })).min(1, 'Please add at least one asset')
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            assetsAttributes: data.assetsAttributes || []
        },
        mode: 'onTouched'
    });

    const assets = form.watch('assetsAttributes');

    const visibleAssets = assets.filter(asset => !asset._destroy);

    const handleToggle = (assetRef, fieldName, newValue) => {
        // Map through ALL assets and only update the one with the matching reference
        const updatedAssets = assets.map(asset => {
            if (asset.asset_reference === assetRef) {
                return {
                    ...asset,
                    [fieldName]: newValue
                };
            }
            return asset;
        });

        form.setValue('assetsAttributes', updatedAssets, { shouldValidate: true });

        // Sync with the parent/Wizard state
        if (onChangeField) {
            onChangeField('assetsAttributes')(updatedAssets);
        }
    };

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid, onCompleted]);

    return (
        <div className="tw-w-full">
            <div className="tw-border tw-border-solid tw-border-border tw-rounded-lg tw-overflow-hidden">
                <table className="tw-w-full tw-text-left tw-border-collapse">
                    <thead className="tw-bg-grey-bg tw-border-b tw-border-solid tw-border-border">
                        <tr>
                            <th className="tw-p-4 tw-text-xs tw-font-bold tw-text-gray-600 tw-uppercase">{i18next.t('integrations.asset_name')}</th>
                            <th className="tw-p-4 tw-text-xs tw-font-bold tw-text-gray-600 tw-uppercase">{i18next.t('integrations.nextbitt.id')}</th>
                            <th className="tw-p-4 tw-text-xs tw-font-bold tw-text-gray-600 tw-uppercase tw-text-center">{i18next.t('integrations.integrate_workorders')}</th>
                            <th className="tw-p-4 tw-text-xs tw-font-bold tw-text-gray-600 tw-uppercase tw-text-center">{i18next.t('integrations.integrate_markers')}</th>
                        </tr>
                    </thead>
                    <tbody className="tw-divide-y">
                        {visibleAssets.map((asset, index) => (
                            <tr className="hover:tw-bg-accent tw-transition-colors" key={asset.asset_reference}>
                                <td className="tw-p-4 tw-border-r tw-border-solid tw-border-border">
                                    <div className="tw-font-medium tw-text-sm tw-text-gray-700">{asset.name}</div>
                                </td>
                                <td className="tw-p-4 tw-border-r tw-border-solid tw-border-border">
                                    <code className="tw-text-[10px] tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-rounded">
                                        {asset.asset_reference}
                                    </code>
                                </td>
                                <td className="tw-p-4 tw-border-r tw-border-solid tw-border-border">
                                    <div className="tw-flex tw-justify-center">
                                        <Switch
                                            checked={asset.create_work_orders}
                                            onCheckedChange={val => handleToggle(asset.asset_reference, 'create_work_orders', val)}
                                        />
                                    </div>
                                </td>
                                <td className="tw-p-4 tw-border-none">
                                    <div className="tw-flex tw-justify-center">
                                        <Switch
                                            checked={asset.create_markers}
                                            onCheckedChange={val => handleToggle(asset.asset_reference, 'create_markers', val)}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {form.formState.errors.assetsAttributes && (
                <p className="tw-text-red-500 tw-text-xs tw-mt-2">
                    {form.formState.errors.assetsAttributes.message}
                </p>
            )}
        </div>
    );
}
